import { useEffect, useMemo, useState } from 'react'
import { useQueryParams } from '../useQueryParams'

type UseSyncFilterFE<Value> = {
  name: string
  selectedOptions: Value[]
  setSelectedOptions?: ((val: Value[]) => void) | undefined
}

// Unlike useSyncFilter, this doesn't require a filterConfig object, so is a
// bit more flexible and suited towards FE filtering patterns that don't directly
// integrate with a BE API and/or an AntD table.
export const useSyncFilterFE = <Value>({
  name,
  selectedOptions,
  setSelectedOptions,
}: UseSyncFilterFE<Value>) => {
  const { getQueryParam, replaceQueryParam, removeQueryParams } = useQueryParams()

  const stringifiedSelectedOptions = selectedOptions.length ? JSON.stringify(selectedOptions) : ''
  const selectedOptionsFromURL = getQueryParam(name)
  const parsedOptionsFromURL = useMemo(
    () => (selectedOptionsFromURL ? (JSON.parse(selectedOptionsFromURL) as Value[]) : []),
    [selectedOptionsFromURL],
  )

  const [initialLoadFromURL, setInitialLoadFromURL] = useState(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: too many deps
  useEffect(() => {
    const valuesMatchUrl = stringifiedSelectedOptions === selectedOptionsFromURL

    if (!initialLoadFromURL) {
      if (parsedOptionsFromURL.length > 0) {
        setSelectedOptions?.(parsedOptionsFromURL)
      }
      setInitialLoadFromURL(true)
    } else if (parsedOptionsFromURL.length > 0 && stringifiedSelectedOptions.length === 0) {
      removeQueryParams([name])
    } else if (!valuesMatchUrl && stringifiedSelectedOptions) {
      replaceQueryParam({
        [name]: stringifiedSelectedOptions,
      })
    }
  }, [
    initialLoadFromURL,
    stringifiedSelectedOptions,
    selectedOptionsFromURL,
    getQueryParam,
    replaceQueryParam,
    removeQueryParams,
    setSelectedOptions,
    name,
    parsedOptionsFromURL,
  ])
}
